import React from 'react';
import { TextFieldProps } from '@mui/material';
import { useFormField } from '@core/hooks/useFormField';
import { WcnTextField } from '@/components';

type ValidationRule = (value: any) => string;

interface FormTextFieldProps extends Omit<TextFieldProps, 'error' | 'helperText'> {
    name: string;
    value: any;
    rules?: ValidationRule[];
    labelText?: string;
    helperText?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
}

export const FormTextField: React.FC<FormTextFieldProps> = ({
    name,
    value,
    rules = [],
    helperText: staticHelperText,
    labelText,
    onChange,
    onBlur,
    ...textFieldProps
}) => {
    const fieldLabel = typeof textFieldProps.label === 'string' ? textFieldProps.label : undefined;
    const { error, showError, handleBlur, handleChange } = useFormField({
        name,
        value,
        label: fieldLabel,
        rules,
        onChange: (newValue: any) => {
            // Create synthetic event for onChange compatibility
            if (onChange) {
                const event = {
                    target: { value: newValue }
                } as React.ChangeEvent<HTMLInputElement>;
                onChange(event);
            }
        }
    });

    const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(event.target.value);
    };

    const handleTextFieldBlur = () => {
        handleBlur();
        onBlur?.();
    };

    return (
        <WcnTextField
            {...textFieldProps}
            value={value ?? ''}
            onChange={handleTextFieldChange}
            onBlur={handleTextFieldBlur}
            error={showError}
            helperText={showError ? error : staticHelperText}
            InputLabelProps={{ shrink: true, ...textFieldProps.InputLabelProps }}
            className={`form-text-field${textFieldProps.className ? ` ${textFieldProps.className}` : ''}`}
            variant="outlined"
            autoComplete="off"
        />
    );
};

FormTextField.displayName = 'FormTextField';

export default FormTextField;
